/*
 * @author: wayou
 * @date: 2018-09-04 17:10:14
 * @description:
 * 替代原生图片标签，增加了
 * - 图片加载态
 * - 图片加载失败的回退展示
 * - 延迟加载 （只在支持 `IntersectionObserver` 的环境下才生效）
 */

import React, { Component } from 'react';
import imageApi from '@umu/umu-image-handle';
import { $document } from '@umu/umu-dom';
import { PreImg } from '../preImg/preImg';
import { isSlowNetwork, getNetworkStatus } from 'common/src/services/networkInfo';

import './emg.scss';

export enum EmgFit {
    'default',
    'contain',
    'cover',
}

import LOAD_ERR_IMG from './images/load_err.png';
import LOADING_IMG from './images/loading.png';

export { LOAD_ERR_IMG, LOADING_IMG };

interface IEmgProps {
    src: string;
    className?: string;
    alt?: string;
    title?: string;
    style?: React.CSSProperties;
    fit: EmgFit;

    /**
     * 加载态展示的图片
     */
    loadingImg?: string;

    /**
     * 加载失败展示的图片以标识失败状态，区别于 `fallbackImg`，它看起来是张错误图片
     */
    loadErrImg?: string;

    /**
     * 加载失败时的回退图片以替换正常需要展示的图片，区别于 `loadErrImg`，它看起来是张正常图片
     * 一般是整站的默认占位图片
     */
    fallbackImg?: string;

    /**
     * 是否启用延迟加载
     * 只在支持 [`IntersectionObserver`](https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API)
     * API 的环境下生效，不支持的浏览器则不会有延迟加载的功能
     */
    isLazyLoad?: boolean;

    onLoad?: (event: Event) => void;
    onError?: (event: Event) => void;
}

interface IEmgState {
    isLoading: boolean;
    isLoadSuccess: boolean;
}

const IMAGE_CLASS_NAMES = {
    IMAGE_CLASSNAME: 'emg',
    IMAGE_LOADING: 'emg-loading',
    IMAGE_LOAD_SUCCESS: 'emg-load-success',
    IMAGE_LOAD_ERR: 'emg-load-error',
};

const doc = $document.querySelector('.doc');

class Emg extends Component<IEmgProps, IEmgState> {
    public static defaultProps: Partial<IEmgProps> = {
        className: '',
        alt: '',
        loadingImg: LOADING_IMG,
        loadErrImg: LOAD_ERR_IMG,
        fallbackImg: '',
        isLazyLoad: true,
        fit: EmgFit.default,
    };

    private imageElement?: HTMLImageElement;

    constructor(props: IEmgProps) {
        super(props);

        this.state = {
            isLoading: true,
            isLoadSuccess: false,
        };
    }

    public componentDidMount() {
        const { src, isLazyLoad } = this.props;

        if (false && isLazyLoad && 'IntersectionObserver' in window) {
            this.initalLazyLoad();
        } else {
            this.loadImg(src);
        }
    }

    public render() {
        const { alt, title, style, className, fit } = this.props;
        const { isLoading, isLoadSuccess } = this.state;
        const imgSrc = this.getImgUrl();

        const isDefaultFit = isLoading || fit === EmgFit.default;
        const classNames = this.getClassName(isDefaultFit ? className : '', isLoading, isLoadSuccess);
        const defaultFit = (
            <PreImg>
                <img
                    ref={(ref) => {
                        this.imageElement = ref as HTMLImageElement;
                    }}
                    className={classNames}
                    src={imgSrc}
                    alt={alt}
                    title={title}
                    style={style}
                />
            </PreImg>
        );

        if (!isDefaultFit) {
            let height: string | number = 'auto';
            if (doc && this.props.fit === EmgFit.cover) {
                height = Math.floor(doc.clientWidth / 3 - 7);
            }
            const containStyle = {
                backgroundImage: `url('${imageApi.process(imgSrc)}')`,
            };
            return (
                <div className={`emg-fit-${fit}`} style={{ ...containStyle, ...style }}>
                    <PreImg>
                        <img src={imgSrc} className="emg" style={{ height }} />
                    </PreImg>
                </div>
            );
        }
        return defaultFit;
    }

    private getImgUrl(): string {
        const { src, loadingImg, loadErrImg, fallbackImg } = this.props;
        const { isLoading, isLoadSuccess } = this.state;

        if (isLoading) {
            return loadingImg!;
        } else {
            if (isLoadSuccess) {
                return src;
            } else {
                if (fallbackImg) {
                    return fallbackImg;
                } else {
                    return loadErrImg!;
                }
            }
        }
    }

    private getClassName(className: string = '', isLoading: boolean, isLoadSuccess: boolean): string {
        return [
            IMAGE_CLASS_NAMES.IMAGE_CLASSNAME,
            !!className ? className : '',
            isLoading
                ? IMAGE_CLASS_NAMES.IMAGE_LOADING
                : isLoadSuccess
                ? IMAGE_CLASS_NAMES.IMAGE_LOAD_SUCCESS
                : IMAGE_CLASS_NAMES.IMAGE_LOAD_ERR,
        ].join(' ');
    }

    private onLoad = (event: Event): void => {
        const { onLoad } = this.props;

        this.setState({
            isLoading: false,
            isLoadSuccess: true,
        });

        if (onLoad) {
            onLoad(event);
        }
    };

    private onError = (
        event: Event | string,
        source?: string,
        lineno?: number,
        colno?: number,
        error?: Error,
    ): void => {
        console.error(
            '[common] emg onError: ',
            event,
            source || '',
            lineno || '',
            colno || '',
            error,
            this.props.src || '',
            getNetworkStatus(),
            isSlowNetwork(),
        );

        if (typeof event === 'string') {
            return;
        }

        const { onError } = this.props;
        this.setState({
            isLoading: false,
            isLoadSuccess: false,
        });

        if (onError) {
            onError(event);
        }
    };

    private initalLazyLoad() {
        const imageElement = this.imageElement;
        if (!imageElement) {
            return;
        }

        if ('IntersectionObserver' in window) {
            const lazyImageObserver = new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        const lazyImage = entry.target as HTMLImageElement;
                        this.loadImg(this.props.src);
                        lazyImageObserver.unobserve(lazyImage);
                    }
                });
            });

            lazyImageObserver.observe(imageElement);
        }
    }

    private loadImg(src: string) {
        const loader = new Image();
        loader.onload = this.onLoad;
        loader.onerror = this.onError;
        loader.src = src;
    }
}

export default Emg;
